import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

export type MultiSelectOption = {
  value: string;
  label: string;
};

type MultiSelectFilterProps = {
  label: string;
  options: MultiSelectOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  allLabel?: string;
  className?: string;
};

const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  label,
  options,
  selectedValues,
  onChange,
  allLabel = 'All',
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

  const buttonLabel = useMemo(() => {
    if (selectedValues.length === 0) return allLabel;
    if (selectedValues.length <= 2) {
      const labels = options
        .filter((opt) => selectedSet.has(opt.value))
        .map((opt) => opt.label);
      if (labels.length > 0) return labels.join(', ');
    }
    return `${selectedValues.length} selected`;
  }, [allLabel, options, selectedSet, selectedValues.length]);

  const toggleValue = (value: string) => {
    if (selectedSet.has(value)) {
      onChange(selectedValues.filter((v) => v !== value));
      return;
    }
    onChange([...selectedValues, value]);
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <label className="text-[10px] uppercase tracking-wide text-slate-400 mb-1 block">{label}</label>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full rounded-md bg-slate-800/60 border border-slate-700/60 text-slate-200 text-xs px-2 py-1.5 pr-8 text-left focus:outline-none focus:ring-1 focus:ring-cyan-500"
      >
        <span className="block truncate">{buttonLabel}</span>
        <ChevronDownIcon className={`w-4 h-4 absolute right-2 top-[25px] text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-md border border-slate-700/60 bg-slate-900 shadow-xl max-h-64 overflow-auto">
          <div className="flex items-center justify-between px-2 py-1 border-b border-slate-700/50">
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-[11px] text-cyan-300 hover:text-cyan-200"
            >
              Clear
            </button>
            <span className="text-[11px] text-slate-400">{selectedValues.length} selected</span>
          </div>
          <ul className="py-1">
            {options.map((option) => (
              <li key={option.value}>
                <button
                  type="button"
                  onClick={() => toggleValue(option.value)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-left hover:bg-slate-800/70 text-xs"
                >
                  <input
                    type="checkbox"
                    className="accent-cyan-500"
                    checked={selectedSet.has(option.value)}
                    readOnly
                  />
                  <span className="text-slate-200">{option.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MultiSelectFilter;
